import {
  AfterViewChecked,
  Component,
  computed,
  effect,
  ElementRef,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Sender, UserMessage } from '@sendbird/chat/message';
import { AppService } from 'src/app/app.service';
import { AccountInforData } from 'src/app/shared/interfaces/account.interface';
import { defaultAvatarPc } from 'src/app/shared/interfaces/settings.interface';
import { MatConnectedDialog, MatConnectedDialogMobileAnimation } from 'src/app/shared/service/connected-dialog';
import { LocalStorageService } from 'src/app/shared/service/localstorage.service';
import { ChatInputComponent } from './components/chat-input/chat-input.component';
import { ChatProfileComponent } from './components/chat-profile/chat-profile.component';
import { SendbirdChatService } from './sendbird-chat.service';
@UntilDestroy()
@Component({
  selector: 'app-sendbird-chat',
  templateUrl: './sendbird-chat.component.html',
  styleUrls: ['./sendbird-chat.component.scss'],
})
export class SendBirdChatComponent implements OnInit, AfterViewChecked, OnDestroy {
  private isReady = false;
  private shouldScroll = false;
  private scrollThreshold = 100;
  private initSendbirdPromise: Promise<boolean> | null = null;

  userId = '';
  newMessage = signal('');
  showScrollBottom = false;
  isOpen = signal(false);
  isVisible = signal(false);
  isActive = signal(false);
  isSending = signal(false);
  readonly rawMessages = computed(() => {
    if (!this.showScrollBottom) {
      this.shouldScroll = true; // 如果不顯示滾動按鈕，則自動滾動到底部
    }

    return this.sendbirdChatService.rawMessages();
  });
  readonly replyMessage = this.sendbirdChatService.replyMessage;
  readonly remainingSeconds = this.sendbirdChatService.remainingSeconds;
  readonly restrictionType = this.sendbirdChatService.restrictionType;
  readonly isLoading = this.sendbirdChatService.isLoading;
  readonly isContactSupport = this.sendbirdChatService.isContactSupport;

  @ViewChild('contentRef') contentRef!: ElementRef<HTMLDivElement>;
  @ViewChild('messagesEndRef') messagesEndRef!: ElementRef;
  @ViewChild(ChatInputComponent) chatInputComponent!: any;
  @ViewChild('contentWrapper') contentWrapper!: ElementRef<HTMLDivElement>;
  @ViewChild('chatContainer') chatContainer!: ElementRef<HTMLDivElement>;

  constructor(
    private appService: AppService,
    private localStorageService: LocalStorageService,
    public sendbirdChatService: SendbirdChatService,
    private matConnectedDialog: MatConnectedDialog,
  ) {
    effect(() => {
      if (this.sendbirdChatService.rawMessages().length === 0) {
        this.showScrollBottom = false;
      }
    });

    // 監聽 loading 狀態變化,完成後移除 min-height
    effect(() => {
      const loading = this.isLoading();
      if (!loading && this.contentWrapper?.nativeElement) {
        // loading 完成後移除 min-height
        setTimeout(() => {
          if (this.contentWrapper?.nativeElement) {
            this.contentWrapper.nativeElement.style.minHeight = '';
          }
        }, 200); // 稍微延遲以確保 loading 動畫完成
      }
    });
  }

  private showChat() {
    // 1. 先設置 display: flex
    this.isVisible.set(true);
    // 2. 使用 setTimeout 確保 DOM 更新後再觸發動畫
    setTimeout(() => {
      this.isActive.set(true);
    }, 10);
  }

  private hideChat() {
    // 1. 移除 active class 觸發關閉動畫
    this.isActive.set(false);
    // 2. 等待動畫結束後設置 display: none
    setTimeout(() => {
      this.isVisible.set(false);
    }, 300); // 0.3s 動畫時間
  }

  // 初始化 SendBird
  private async processInitSendBird(): Promise<boolean> {
    if (this.sendbirdChatService.isInitialized()) {
      return true;
    }

    if (this.initSendbirdPromise) {
      return this.initSendbirdPromise;
    }

    const userInfo = this.appService.userInfo$.value;
    if (!userInfo?.uid) {
      this.sendbirdChatService.setGuestMode(true);
      this.sendbirdChatService.checkNeedToLogin();
      this.sendbirdChatService.hideChat();
      return false;
    }

    this.isLoading.set(true);
    this.sendbirdChatService.setGuestMode(false);
    this.initSendbirdPromise = this.sendbirdChatService
      .initSendbird(userInfo.uid, userInfo.chatAccessToken)
      .then(result => {
        if (!result) {
          return false;
        }

        if (!result?.registered || !result.accessToken || userInfo.chatAccessToken === result.accessToken) {
          return true;
        }

        const currentUserInfo = this.appService.userInfo$.value;
        if (currentUserInfo?.uid !== userInfo.uid) {
          return true;
        }

        const updatedUserInfo: AccountInforData = {
          ...currentUserInfo,
          chatAccessToken: result.accessToken,
        };

        this.appService.userInfo$.next(updatedUserInfo);
        this.localStorageService.localUserInfo = updatedUserInfo;
        return true;
      })
      .finally(() => {
        this.initSendbirdPromise = null;
      });

    return this.initSendbirdPromise;
  }

  ngOnInit() {
    this.sendbirdChatService.displayChat$.pipe(untilDestroyed(this)).subscribe(async isOpen => {
      this.isOpen.set(isOpen);

      // 控制顯示/隱藏動畫
      if (isOpen) {
        const shouldShowChat = await this.processInitSendBird();
        if (shouldShowChat && this.isOpen()) {
          this.showChat();
        }
      } else {
        this.hideChat();
      }
    });

    // 監聽瀏覽器關閉事件
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  ngOnDestroy() {
    // 移除瀏覽器關閉事件監聽
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    // Component 銷毀時也清理 Sendbird 連線
    this.sendbirdChatService.cleanupOnBrowserClose();
  }

  private handleBeforeUnload = () => {
    // 瀏覽器關閉時清理並離開 channel
    this.sendbirdChatService.cleanupOnBrowserClose();
  };

  ngAfterViewChecked(): void {
    if (this.shouldScroll && this.contentRef) {
      const el = this.contentRef.nativeElement;
      el.scrollTop = el.scrollHeight;
      this.shouldScroll = false;
    }

    if (!this.isReady) {
      this.isReady = true;
      const container = this.contentRef.nativeElement;
      container.addEventListener('scroll', this.onScroll);
    }
  }

  async handleSendMessage() {
    if (this.sendbirdChatService.checkNeedToLogin()) {
      return;
    }

    const msg = this.newMessage();
    if (!msg || this.isSending()) {
      return;
    }

    this.newMessage.set('');
    this.isSending.set(true);

    try {
      await this.sendbirdChatService.sendMessage(msg, this.chatInputComponent.getMentionList());
      // 重置閒置計時器
      this.sendbirdChatService.resetIdleTimer();
    } catch (error) {
      console.error('Message send failed:', error);
    } finally {
      this.isSending.set(false);
    }
  }

  handleInputChange(text: string) {
    this.newMessage.set(text);
  }

  onScroll = () => {
    const container = this.contentRef.nativeElement;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
    this.showScrollBottom = distanceFromBottom > this.scrollThreshold;
  };

  scrollToBottom() {
    if (this.contentRef) {
      const el = this.contentRef.nativeElement;
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      this.shouldScroll = false;
    }
  }

  /**
   *
   * @param msg 回覆消息
   */
  handleReplyMessage(msg: UserMessage): void {
    if (this.sendbirdChatService.checkNeedToLogin()) {
      return;
    }
    this.sendbirdChatService.setReplyMessage(msg);
    console.debug('Replying to message:', msg);
  }

  /**
   * 取消回覆消息
   */
  handleCancelReplayMessage(): void {
    this.sendbirdChatService.setReplyMessage(null);
    console.debug('Cancelled reply message');
  }

  handleClickReplyMessage(msg: UserMessage): void {
    const index = this.rawMessages().findIndex(item => item.message.messageId === msg.messageId);
    if (index !== -1) {
      const element = this.contentRef.nativeElement.children[index];
      if (element) {
        const observer = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting) {
            observer.disconnect();
            element.classList.add('blink');
          }
        });

        observer.observe(element);
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }
  handleBlinkAnimEnd(index: number): void {
    const element = this.contentRef.nativeElement.children[index];
    if (element) {
      element.classList.remove('blink');
    }
  }

  handleClickUsername(sender: Sender): void {
    if (this.sendbirdChatService.checkNeedToLogin()) {
      return;
    }
    const selAvatar: defaultAvatarPc | undefined = this.appService.avatarList.find(
      (v: defaultAvatarPc) => v.idx === sender?.plainProfileUrl,
    );

    let avatarUrl: string = '';
    if (selAvatar?.processedUrl) {
      avatarUrl = selAvatar?.processedUrl;
    } else {
      avatarUrl = this.appService.avatarList[0]?.processedUrl || '';
    }

    this.matConnectedDialog.open(ChatProfileComponent, {
      mobileAnimation: MatConnectedDialogMobileAnimation.SLIDE_UP,
      mobileFixedBottom: true,
      data: {
        sender,
        avatarUrl,
      },
    });
  }
}
